import React from 'react';
import AgencyNavbar from '../components/agencies/AgencyNavbar';
import DiscoveryCta from '../components/agencies/DiscoveryCta';
import Footer from '../components/layout/Footer';
import VideoSlot from '../components/marketing/VideoSlot';
import SafeIcon from '../common/SafeIcon';
import PartnerPaths from '../components/agencies/PartnerPaths';

const transformations = [
  {
    from: 'RepUpgrade',
    to: 'Northstar Local Trust',
    detail: 'Illustrative product-name transformation',
  },
  {
    from: 'Ruby, Mabel & Annie',
    to: 'Your named specialist team',
    detail: 'Customer-facing identities shaped for your market',
  },
  {
    from: 'RepUpgrade visual system',
    to: 'Your logo, colors and typography',
    detail: 'Approved brand treatment across the client experience',
  },
  {
    from: 'RepUpgrade positioning',
    to: 'Your niche promise and packaging',
    detail: 'A market-facing offer led by your company',
  },
];

const controls = [
  'Market-facing system name',
  'Customer-facing agent identities',
  'Approved logo, colors, typography, and brand language',
  'Positioning, packaging, and client-facing pricing',
  'The client relationship and front-stage experience',
  'One approved niche or geographic market',
];

export default function WhiteLabelPartner() {
  return (
    <div className="min-h-screen overflow-x-hidden bg-[#F7F8F7] font-lexend text-[#101F1F]">
      <AgencyNavbar />

      <main id="main-content">
        <section className="px-6 pb-20 pt-36 lg:px-8 lg:pb-28 lg:pt-44">
          <div className="mx-auto max-w-6xl">
            <div className="max-w-4xl">
              <p className="font-michroma text-xs font-bold uppercase tracking-[0.2em] text-[#409FA1]">
                White Label Partner · Full White Label
              </p>
              <h1 className="mt-6 text-balance text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-7xl">
                Their customer stories. Your branded reputation system.
              </h1>
              <p className="mt-7 max-w-3xl text-lg leading-8 text-[#244A49]/75 lg:text-xl">
                Help clients turn customer feedback into replies, articles, and social
                content through a system with your brand and agent names. Built
                for approved Agency Partner Dominate members who demonstrate 25+ businesses, with a defined implementation scope.
              </p>
              <p className="mt-5 max-w-3xl font-semibold leading-7 text-[#244A49]">
                Your company leads the client experience. RepUpgrade provides
                the licensed foundation and agreed implementation support. The build is $9,949 one time, with ongoing platform and licensing charges separate. Optional first-year coaching is quoted as an agreed percentage in your proposal.
              </p>

              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <DiscoveryCta interest="white-label" />
                <a
                  href="#branding-preview"
                  className="inline-flex min-h-12 items-center justify-center rounded-xl border border-[#244A49]/20 bg-white px-7 py-4 font-bold text-[#244A49] transition hover:border-[#409FA1] hover:text-[#409FA1]"
                >
                  See the Branding Example
                </a>
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-5xl px-6 pb-16"><VideoSlot name="enterprise" /></div>
        <section
          id="branding-preview"
          className="bg-[#101F1F] px-6 py-20 text-white lg:px-8 lg:py-28"
        >
          <div className="mx-auto max-w-7xl">
            <div className="max-w-4xl">
              <p className="font-michroma text-xs font-bold uppercase tracking-[0.2em] text-[#2CB5B0]">
                Illustrative Transformation
              </p>
              <h2 className="mt-4 text-balance text-3xl font-bold tracking-tight sm:text-5xl">
                The foundation stays coordinated. The market-facing identity
                becomes yours.
              </h2>
              <p className="mt-6 text-lg leading-8 text-white/70">
                “Northstar Local Trust” is a fictional example used only to
                demonstrate how an approved white-label implementation could
                transform the customer-facing experience.
              </p>
            </div>

            <div className="mt-12 overflow-hidden border border-white/15">
              <div className="hidden grid-cols-[1fr_auto_1fr] border-b border-white/15 bg-white/[0.05] px-7 py-4 text-xs font-bold uppercase tracking-widest text-white/50 md:grid">
                <span>RepUpgrade foundation</span>
                <span />
                <span>Your branded experience</span>
              </div>
              {transformations.map((item) => (
                <div
                  key={item.from}
                  className="grid gap-4 border-b border-white/10 px-6 py-7 last:border-b-0 md:grid-cols-[1fr_auto_1fr] md:items-center md:px-7"
                >
                  <div>
                    <p className="text-xs uppercase tracking-wide text-white/40 md:hidden">
                      Foundation
                    </p>
                    <p className="mt-1 font-bold">{item.from}</p>
                  </div>
                  <SafeIcon
                    name="ArrowRight"
                    className="h-5 w-5 rotate-90 text-[#F6D541] md:rotate-0"
                  />
                  <div>
                    <p className="text-xs uppercase tracking-wide text-white/40 md:hidden">
                      Branded experience
                    </p>
                    <p className="mt-1 text-lg font-bold text-[#2CB5B0]">
                      {item.to}
                    </p>
                    <p className="mt-2 text-sm text-white/55">{item.detail}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-5 text-sm leading-6 text-white/45">
              This example is illustrative, not a live partner brand or a
              promise of unrestricted customization. Final implementation is
              governed by the signed scope.
            </p>
          </div>
        </section>

        <section className="border-b border-[#244A49]/10 bg-white px-6 py-20 lg:px-8 lg:py-28">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <p className="font-michroma text-xs font-bold uppercase tracking-[0.2em] text-[#409FA1]">
                Market-Facing Control
              </p>
              <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-5xl">
                Build around the market you already understand.
              </h2>
              <p className="mt-6 text-lg leading-8 text-[#244A49]/70">
                The white-label path is designed for agencies ready to invest
                in a distinct reputation brand for one niche or geographic
                market.
              </p>
            </div>

            <ul className="space-y-4">
              {controls.map((control) => (
                <li
                  key={control}
                  className="flex gap-3 border-b border-[#244A49]/10 pb-4 leading-7 text-[#244A49]/75"
                >
                  <SafeIcon
                    name="CheckCircle"
                    className="mt-1 h-5 w-5 shrink-0 text-[#2CB5B0]"
                  />
                  {control}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <PartnerPaths />
      </main>

      <Footer />
    </div>
  );
}